import React, { useLayoutEffect, useMemo, useRef } from "react";

const VARIABLE_CLASS_NAME =
  "text-violet-200 bg-violet-600/15 ring-1 ring-violet-500/30 rounded-sm shadow-[0_0_12px_rgba(139,92,246,0.35)] [box-decoration-break:clone] [-webkit-box-decoration-break:clone]";

export function normalizePromptValue(value = "") {
  return String(value ?? "").replace(/\r\n?/g, "\n");
}

export function escapeHtml(s = "") {
  return String(s)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function renderTextHtml(text = "") {
  return escapeHtml(text).replace(/\n/g, "<br>");
}

function findPromptVariableRanges(text) {
  const source = String(text ?? "");
  const ranges = [];
  let searchFrom = 0;

  while (searchFrom < source.length) {
    const start = source.indexOf("{{", searchFrom);
    if (start === -1) break;

    const close = source.indexOf("}}", start + 2);
    if (close === -1) break;

    const inner = source.slice(start + 2, close);
    if (inner.length > 0 && !inner.includes("}")) {
      ranges.push({ start, end: close + 2 });
      searchFrom = close + 2;
    } else {
      searchFrom = start + 2;
    }
  }

  return ranges;
}

export function renderPromptHtml(text = "", highlightVariables = true) {
  const normalized = normalizePromptValue(text);
  if (!normalized) return "";
  if (!highlightVariables) return renderTextHtml(normalized);

  const parts = [];
  let lastIndex = 0;

  for (const range of findPromptVariableRanges(normalized)) {
    if (range.start > lastIndex) {
      parts.push(renderTextHtml(normalized.slice(lastIndex, range.start)));
    }

    parts.push(
      `<span class="${VARIABLE_CLASS_NAME}">${renderTextHtml(normalized.slice(range.start, range.end))}</span>`
    );
    lastIndex = range.end;
  }

  if (lastIndex < normalized.length) {
    parts.push(renderTextHtml(normalized.slice(lastIndex)));
  }

  return parts.join("");
}

export function getPlainTextFromNode(node) {
  if (!node) return "";

  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent ?? "";
  }

  if (node.nodeType === Node.ELEMENT_NODE) {
    if (node.nodeName === "BR") return "\n";
    return Array.from(node.childNodes).map((child) => getPlainTextFromNode(child)).join("");
  }

  if (node.nodeType === Node.DOCUMENT_FRAGMENT_NODE) {
    return Array.from(node.childNodes).map((child) => getPlainTextFromNode(child)).join("");
  }

  return "";
}

function getNodeTextLength(node) {
  return getPlainTextFromNode(node).length;
}

export function getSelectionOffsets(root) {
  if (!root || typeof window === "undefined" || typeof document === "undefined") {
    return null;
  }

  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return null;

  const range = selection.getRangeAt(0);
  if (!root.contains(range.startContainer) || !root.contains(range.endContainer)) {
    return null;
  }

  const startRange = document.createRange();
  startRange.selectNodeContents(root);
  startRange.setEnd(range.startContainer, range.startOffset);

  const endRange = document.createRange();
  endRange.selectNodeContents(root);
  endRange.setEnd(range.endContainer, range.endOffset);

  return {
    start: getNodeTextLength(startRange.cloneContents()),
    end: getNodeTextLength(endRange.cloneContents()),
  };
}

export function resolveSelectionPoint(node, offset) {
  const safeOffset = Math.max(0, Number(offset) || 0);

  if (node.nodeType === Node.TEXT_NODE) {
    const textLength = node.textContent?.length ?? 0;
    return { node, offset: Math.min(safeOffset, textLength) };
  }

  if (node.nodeType === Node.ELEMENT_NODE && node.nodeName === "BR") {
    const parent = node.parentNode;
    if (!parent) {
      return { node, offset: 0 };
    }

    const index = Array.prototype.indexOf.call(parent.childNodes, node);
    return {
      node: parent,
      offset: safeOffset <= 0 ? index : index + 1,
    };
  }

  const children = Array.from(node.childNodes || []);
  if (!children.length) {
    return { node, offset: 0 };
  }

  let remaining = safeOffset;
  for (const child of children) {
    const childLength = getNodeTextLength(child);
    if (remaining <= childLength) {
      return resolveSelectionPoint(child, remaining);
    }
    remaining -= childLength;
  }

  return { node, offset: children.length };
}

export function setSelectionOffsets(root, start, end = start) {
  if (!root || typeof window === "undefined" || typeof document === "undefined") {
    return false;
  }

  const totalLength = getNodeTextLength(root);
  const safeStart = Math.max(0, Math.min(Number(start) || 0, totalLength));
  const safeEnd = Math.max(safeStart, Math.min(Number(end) || 0, totalLength));

  const startPoint = resolveSelectionPoint(root, safeStart);
  const endPoint = resolveSelectionPoint(root, safeEnd);
  const selection = window.getSelection();

  if (!selection) return false;

  const range = document.createRange();
  range.setStart(startPoint.node, startPoint.offset);
  range.setEnd(endPoint.node, endPoint.offset);

  selection.removeAllRanges();
  selection.addRange(range);
  return true;
}

export function insertTextAtOffsets(value, selection, insertedText) {
  const currentValue = normalizePromptValue(value);
  const textToInsert = String(insertedText ?? "");
  const rawStart = Number(selection?.start);
  const rawEnd = Number(selection?.end);
  const start = Math.max(
    0,
    Math.min(Number.isFinite(rawStart) ? rawStart : currentValue.length, currentValue.length)
  );
  const end = Math.max(
    start,
    Math.min(Number.isFinite(rawEnd) ? rawEnd : start, currentValue.length)
  );
  const nextValue = currentValue.slice(0, start) + textToInsert + currentValue.slice(end);
  const nextCaret = start + textToInsert.length;

  return {
    value: nextValue,
    selection: {
      start: nextCaret,
      end: nextCaret,
    },
  };
}

function applyEditorHtml(editor, value, selection) {
  if (!editor) return;
  const html = renderPromptHtml(value, true);
  if (editor.innerHTML !== html) {
    editor.innerHTML = html;
  }
  if (selection) {
    setSelectionOffsets(editor, selection.start, selection.end);
  }
}

export default function ScenarioPromptEditor({
  value,
  onChange,
  placeholder = "",
  variables = [],
  disabled = false,
  editorClassName = "h-[520px] max-h-[65vh]",
  showVariableChips = true,
  highlightVariables = true,
}) {
  const editorRef = useRef(null);
  const textareaRef = useRef(null);
  const lastSelectionRef = useRef(null);
  const pendingSelectionRef = useRef(null);
  const pendingTextareaSelectionRef = useRef(null);
  const isComposingRef = useRef(false);

  const normalizedValue = useMemo(() => normalizePromptValue(value), [value]);
  const normalizedPlaceholder = useMemo(
    () => normalizePromptValue(placeholder),
    [placeholder]
  );

  const wrapClass = [
    "relative rounded-2xl border border-[#1f2638] bg-[#0f1523] overflow-hidden",
    "focus-within:ring-2 focus-within:ring-violet-600/40 focus-within:border-violet-500/40",
    editorClassName,
  ].join(" ");

  const editorClass = [
    "absolute inset-0",
    "m-0 p-3",
    "text-sm font-mono leading-6 text-slate-200",
    "whitespace-pre-wrap break-words",
    "overflow-auto",
    "nice-scrollbar",
    "box-border",
    "outline-none",
    disabled ? "cursor-not-allowed opacity-80" : "",
  ].join(" ");

  const rememberSelection = () => {
    const editor = editorRef.current;
    const selection = getSelectionOffsets(editor);
    if (selection) {
      lastSelectionRef.current = selection;
    }
  };

  const syncFromEditor = ({ forceRender = false } = {}) => {
    const editor = editorRef.current;
    if (!editor) return;

    const nextValue = normalizePromptValue(getPlainTextFromNode(editor));
    const selection =
      getSelectionOffsets(editor) || {
        start: nextValue.length,
        end: nextValue.length,
      };

    lastSelectionRef.current = selection;
    pendingSelectionRef.current = selection;

    if (nextValue !== normalizedValue) {
      onChange?.(nextValue);
      return;
    }

    if (forceRender && !isComposingRef.current) {
      applyEditorHtml(editor, normalizedValue, selection);
    }
  };

  useLayoutEffect(() => {
    if (!highlightVariables) return;

    const editor = editorRef.current;
    if (!editor || isComposingRef.current) return;

    const shouldKeepSelection = document.activeElement === editor;
    const selection = pendingSelectionRef.current || (shouldKeepSelection ? lastSelectionRef.current : null);
    const nextHtml = renderPromptHtml(normalizedValue, true);
    const nextText = getPlainTextFromNode(editor);

    if (editor.innerHTML !== nextHtml || nextText !== normalizedValue) {
      editor.innerHTML = nextHtml;
    }

    if (selection) {
      editor.focus();
      setSelectionOffsets(editor, selection.start, selection.end);
      lastSelectionRef.current = selection;
    }

    pendingSelectionRef.current = null;
  }, [highlightVariables, normalizedValue]);

  useLayoutEffect(() => {
    if (highlightVariables) return;

    const textarea = textareaRef.current;
    const selection = pendingTextareaSelectionRef.current;
    if (!textarea || !selection) return;

    textarea.focus();
    textarea.setSelectionRange(selection.start, selection.end);
    pendingTextareaSelectionRef.current = null;
  }, [highlightVariables, normalizedValue]);

  const insertVariable = (token) => {
    if (disabled) return;

    if (highlightVariables) {
      const editor = editorRef.current;
      const selection =
        getSelectionOffsets(editor) ||
        lastSelectionRef.current || {
          start: normalizedValue.length,
          end: normalizedValue.length,
        };
      const next = insertTextAtOffsets(normalizedValue, selection, token);

      lastSelectionRef.current = next.selection;
      pendingSelectionRef.current = next.selection;
      onChange?.(next.value);
      return;
    }

    const textarea = textareaRef.current;
    const next = insertTextAtOffsets(
      normalizedValue,
      {
        start: textarea?.selectionStart,
        end: textarea?.selectionEnd,
      },
      token
    );

    pendingTextareaSelectionRef.current = next.selection;
    onChange?.(next.value);
  };

  return (
    <div className="space-y-2">
      <div className={wrapClass}>
        {highlightVariables ? (
          <>
            {!normalizedValue && normalizedPlaceholder && (
              <div
                aria-hidden="true"
                className={[
                  "pointer-events-none absolute inset-0 m-0 p-3",
                  "text-sm font-mono leading-6 text-slate-500",
                  "whitespace-pre-wrap break-words",
                  "box-border",
                ].join(" ")}
              >
                {normalizedPlaceholder}
              </div>
            )}

            <div
              ref={editorRef}
              role="textbox"
              aria-multiline="true"
              aria-disabled={disabled}
              suppressContentEditableWarning
              contentEditable={!disabled}
              spellCheck={false}
              className={editorClass}
              style={{ scrollbarGutter: "stable both-edges" }}
              onInput={() => syncFromEditor()}
              onKeyUp={rememberSelection}
              onMouseUp={rememberSelection}
              onFocus={rememberSelection}
              onBlur={rememberSelection}
              onPaste={(e) => {
                e.preventDefault();
                const text = normalizePromptValue(e.clipboardData?.getData("text/plain") || "");
                if (!text) return;

                const selection =
                  getSelectionOffsets(editorRef.current) ||
                  lastSelectionRef.current || {
                    start: normalizedValue.length,
                    end: normalizedValue.length,
                  };
                const next = insertTextAtOffsets(normalizedValue, selection, text);

                lastSelectionRef.current = next.selection;
                pendingSelectionRef.current = next.selection;
                onChange?.(next.value);
              }}
              onCompositionStart={() => {
                isComposingRef.current = true;
              }}
              onCompositionEnd={() => {
                isComposingRef.current = false;
                syncFromEditor({ forceRender: true });
              }}
            />
          </>
        ) : (
          <textarea
            ref={textareaRef}
            value={normalizedValue}
            onChange={(e) => onChange?.(normalizePromptValue(e.target.value))}
            spellCheck={false}
            disabled={disabled}
            placeholder={normalizedPlaceholder}
            className={[
              editorClass,
              "resize-none bg-transparent",
            ].join(" ")}
            style={{ scrollbarGutter: "stable both-edges" }}
          />
        )}
      </div>

      {showVariableChips && Array.isArray(variables) && variables.length > 0 && (
        <div className="pt-1">
          <div className="mb-2 text-[11px] uppercase tracking-wider text-slate-400">
            {"Помощни променливи"}
          </div>
          <div className="flex flex-wrap gap-2 text-xs">
            {variables.map((token) => (
              <button
                key={token}
                type="button"
                disabled={disabled}
                className="rounded-xl border border-[#1f2638] bg-[#121825] px-2 py-1 text-slate-200 hover:bg-[#141b2d] disabled:opacity-60"
                onMouseDown={(e) => {
                  e.preventDefault();
                  rememberSelection();
                }}
                onClick={() => insertVariable(token)}
              >
                {token}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
